/**
 * Navigation store for page-based survey navigation.
 */

import { create } from 'zustand';
import { telemetryService, SessionEventType } from '@/lib/api';

interface NavigationState {
  // Current position
  currentPage: number;
  totalPages: number;
  currentQuestionIndex: number;

  // Page history
  visitedPages: number[];

  // Question visibility (managed by logic evaluation)
  hiddenQuestions: Set<string>;

  // Display order for randomized options (question code -> option indices)
  displayOrders: Record<string, number[]>;

  // Navigation state
  isNavigating: boolean;
  navigationError: string | null;

  // Computed properties
  canGoBack: boolean;
  canGoForward: boolean;
  isFirstPage: boolean;
  isLastPage: boolean;

  // Actions
  initialize: (totalPages: number, startPage?: number) => void;
  setTotalPages: (totalPages: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  nextPage: () => void;
  previousPage: () => void;
  goToPage: (page: number) => void;
  goToQuestion: (questionCode: string, questionIndex: number) => void;
  skipToQuestion: (questionCode: string, questions: { code: string; page: number }[]) => void;
  skipToPage: (page: number) => void;
  skipToEnd: () => void;
  setHiddenQuestions: (codes: Set<string>) => void;
  addHiddenQuestion: (code: string) => void;
  removeHiddenQuestion: (code: string) => void;
  setDisplayOrder: (questionCode: string, order: number[]) => void;
  setNavigating: (isNavigating: boolean) => void;
  setNavigationError: (error: string | null) => void;
  reset: () => void;
}

export const useNavigationStore = create<NavigationState>((set, get) => ({
  // Initial state
  currentPage: 1,
  totalPages: 1,
  currentQuestionIndex: 0,
  visitedPages: [1],
  hiddenQuestions: new Set(),
  displayOrders: {},
  isNavigating: false,
  navigationError: null,
  canGoBack: false,
  canGoForward: true,
  isFirstPage: true,
  isLastPage: false,

  // Initialize navigation
  initialize: (totalPages: number, startPage = 1) => {
    set({
      totalPages,
      currentPage: startPage,
      currentQuestionIndex: 0,
      visitedPages: [startPage],
      isFirstPage: startPage === 1,
      isLastPage: startPage === totalPages,
      canGoBack: startPage > 1,
      canGoForward: startPage < totalPages,
    });
  },

  // Set total pages
  setTotalPages: (totalPages: number) => {
    const { currentPage } = get();
    set({
      totalPages,
      isLastPage: currentPage >= totalPages,
      canGoForward: currentPage < totalPages,
    });
  },

  // Go to next page
  goToNextPage: () => {
    const { currentPage, totalPages, visitedPages } = get();
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      const newVisitedPages = visitedPages.includes(newPage)
        ? visitedPages
        : [...visitedPages, newPage];

      // Track navigation in telemetry
      telemetryService.track(SessionEventType.NAVIGATION, {
        fromQuestion: `page_${currentPage}`,
        toQuestion: `page_${newPage}`,
        reason: 'next',
      });

      set({
        currentPage: newPage,
        currentQuestionIndex: 0,
        visitedPages: newVisitedPages,
        isFirstPage: false,
        isLastPage: newPage === totalPages,
        canGoBack: true,
        canGoForward: newPage < totalPages,
      });
    }
  },

  // Alias for goToNextPage
  nextPage: () => {
    get().goToNextPage();
  },

  // Go to previous page
  goToPreviousPage: () => {
    const { currentPage } = get();
    if (currentPage > 1) {
      const newPage = currentPage - 1;

      // Track navigation back in telemetry
      telemetryService.track(SessionEventType.NAVIGATION_BACK, {
        fromQuestion: `page_${currentPage}`,
        toQuestion: `page_${newPage}`,
        reason: 'back',
      });

      set({
        currentPage: newPage,
        currentQuestionIndex: 0,
        isFirstPage: newPage === 1,
        isLastPage: false,
        canGoBack: newPage > 1,
        canGoForward: true,
      });
    }
  },

  // Alias for goToPreviousPage
  previousPage: () => {
    get().goToPreviousPage();
  },

  // Go to specific page
  goToPage: (page: number) => {
    const { currentPage, totalPages, visitedPages } = get();
    if (page >= 1 && page <= totalPages) {
      const newVisitedPages = visitedPages.includes(page)
        ? visitedPages
        : [...visitedPages, page];

      // Track jump navigation in telemetry
      telemetryService.track(SessionEventType.NAVIGATION_JUMP, {
        fromQuestion: `page_${currentPage}`,
        toQuestion: `page_${page}`,
        reason: 'jump',
      });

      set({
        currentPage: page,
        currentQuestionIndex: 0,
        visitedPages: newVisitedPages,
        isFirstPage: page === 1,
        isLastPage: page === totalPages,
        canGoBack: page > 1,
        canGoForward: page < totalPages,
      });
    }
  },

  // Go to a specific question on the current page
  goToQuestion: (_questionCode: string, questionIndex: number) => {
    set({ currentQuestionIndex: questionIndex });
  },

  // Skip to a question (find its page)
  skipToQuestion: (questionCode: string, questions: { code: string; page: number }[]) => {
    const question = questions.find((q) => q.code === questionCode);
    if (question) {
      get().goToPage(question.page);
    }
  },

  // Skip to page
  skipToPage: (page: number) => {
    get().goToPage(page);
  },

  // Skip to end (last page)
  skipToEnd: () => {
    const { currentPage, totalPages } = get();

    // Track skip to end in telemetry
    telemetryService.track(SessionEventType.NAVIGATION_JUMP, {
      fromQuestion: `page_${currentPage}`,
      toQuestion: `page_${totalPages}`,
      reason: 'skip_to_end',
    });

    set({
      currentPage: totalPages,
      isFirstPage: false,
      isLastPage: true,
      canGoBack: true,
      canGoForward: false,
    });
  },

  // Set hidden questions
  setHiddenQuestions: (codes: Set<string>) => {
    set({ hiddenQuestions: codes });
  },

  // Add a hidden question
  addHiddenQuestion: (code: string) => {
    set((state) => {
      const newHidden = new Set(state.hiddenQuestions);
      newHidden.add(code);
      return { hiddenQuestions: newHidden };
    });
  },

  // Remove a hidden question
  removeHiddenQuestion: (code: string) => {
    set((state) => {
      const newHidden = new Set(state.hiddenQuestions);
      newHidden.delete(code);
      return { hiddenQuestions: newHidden };
    });
  },

  // Set display order for a question
  setDisplayOrder: (questionCode: string, order: number[]) => {
    set((state) => ({
      displayOrders: {
        ...state.displayOrders,
        [questionCode]: order,
      },
    }));
  },

  // Set navigating state
  setNavigating: (isNavigating: boolean) => {
    set({ isNavigating });
  },

  // Set navigation error
  setNavigationError: (error: string | null) => {
    set({ navigationError: error });
  },

  // Reset
  reset: () => {
    set({
      currentPage: 1,
      totalPages: 1,
      currentQuestionIndex: 0,
      visitedPages: [1],
      hiddenQuestions: new Set(),
      displayOrders: {},
      isNavigating: false,
      navigationError: null,
      canGoBack: false,
      canGoForward: true,
      isFirstPage: true,
      isLastPage: false,
    });
  },
}));
